import MessageCenter from '@c/dashboard/MessageCenter';
import FormLabel from '@c/forms/controls/FormLabel';
import { PencilSimpleIcon } from '@c/icons';
import BaseModal from '@c/modals/BaseModal';
import { TrashIcon } from '@heroicons/react/20/solid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Input from '@ui/Input';
import Select from '@ui/Select';
import Toggle from '@ui/Toggle';
import { AUTH } from '@util/firebase';
import { getChatsForSellerAdminControls } from '@util/firestore/messages';
import { chargeAccount, createTransfer } from '@util/firestore/payments';
import { deleteAllListings, setVacationMode } from '@util/firestore/products';
import {
  addNewSellerNote,
  checkPhoneNumber,
  deleteSellerNote,
  getAuthUserByUid,
  getUserById,
  getUserByUsernameSlug,
  getUserToken,
  signOutUserCallable,
  unlinkProviderCallable,
  updateAuthUser,
  updateRating,
  updateSellerType,
  updateUserByKey,
  updateUserMetricsByKey,
  updateUsername,
} from '@util/firestore/users';
import { logError } from '@util/logError';
import AuthProvider, { useAuth } from 'context/AuthContext';
import { useChat } from 'context/ChatContext';
import { useToastContext } from 'context/ToastContext';
import { fetchSignInMethodsForEmail } from 'firebase/auth';
import { increment } from 'firebase/firestore';
import { parsePhoneNumber } from 'libphonenumber-js';
import { SellerType } from 'models/shared';
import { PublicUserDocument } from 'models/user';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { Button } from '..';

const SellerAdminControls = ({ doc }: { doc: PublicUserDocument }) => {
  const router = useRouter();
  const [openModal, setOpenModal] = useState<
    'updateSellerType' | 'notes' | 'chat-history' | ''
  >('');
  const { showSuccessToast, showErrorToast } = useToastContext();
  const { userDoc, signInWithToken } = useAuth();
  const { openChatModalWithRecipientAndProductId } = useChat();
  const { data: userByUid } = useQuery({
    queryKey: ['user', doc.uid],
    queryFn: () => getUserById(doc.uid),
    enabled: !!doc.uid && userDoc?.roles?.admin,
  });
  const { data: userByUsername } = useQuery({
    queryKey: ['user', doc.username_slug],
    queryFn: () => getUserByUsernameSlug(doc.username_slug),
    enabled: !!doc.username_slug && userDoc?.roles?.admin,
  });

  const user = userByUid ?? userByUsername ?? null;
  const { data: authUser } = useQuery({
    queryKey: ['authUser', user?.uid],
    queryFn: () => getAuthUserByUid(user?.uid),
    enabled: !!user?.uid && userDoc?.roles?.admin,
  });
  const { data: allChats } = useQuery({
    queryKey: ['sellerAdminChats', user?.uid],
    queryFn: () => getChatsForSellerAdminControls(user?.uid!),
    enabled:
      !!user?.uid && userDoc?.roles?.admin && openModal === 'chat-history',
  });
  const [loadingBtn, setLoadingBtn] = useState('');
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sellerType, setSellerType] = useState<SellerType>(
    user?.seller_type ?? 'individual'
  );

  const queryClient = useQueryClient();
  const [newNote, setNewNote] = useState('');

  const deleteAllListingsMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('No user doc');
      await deleteAllListings(user.uid);
    },
    onError: (err) => {
      alert((err as Error).message);
    },
    onSuccess: () => {
      showSuccessToast('All listings deleted');
    },
    onSettled: () => {
      setLoadingBtn('');
    },
  });

  if (!user || !userDoc?.roles?.admin) return null;

  const impersonate = async () => {
    setLoadingBtn('impersonate');
    try {
      const token = await getUserToken({
        emailOrUsername: '',
        uid: user.uid,
      });
      if (token) {
        await signInWithToken({ email: token, password: '' });
        window.location.reload();
      } else alert('User not found');
    } catch (e) {
      logError(e);
    } finally {
      setLoadingBtn('');
    }
  };

  const PROVIDERS: string[] = ['google.com', 'facebook.com', 'apple.com'];
  const unlinkProvider = async () => {
    const provider = prompt(
      'Enter provider to unlink (google.com, facebook.com, apple.com)'
    )
      ?.trim()
      .toLowerCase();
    if (!provider) return;
    if (!PROVIDERS.includes(provider as AuthProvider)) {
      return alert(
        'Invalid provider. Enter either google.com, facebook.com, or apple.com'
      );
    }
    const yes = confirm(`Are you sure you want to unlink ${provider}?`);
    if (yes) {
      setLoadingBtn('unlinkProvider');

      try {
        setLoadingBtn('unlinkProvider');
        await unlinkProviderCallable({
          uid: user.uid,
          provider,
        });
        showSuccessToast('Provider unlinked');
      } catch (e) {
        alert((e as Error)?.message || e);
      } finally {
        setLoadingBtn('');
      }
    }
  };

  const toggleVacation = () => {
    const text = user.on_vacation
      ? 'Are you sure you want to turn off vacation mode? This will make all listings visible again.'
      : 'Are you sure you want to turn on vacation mode? This will temporarily remove all listings until turned back on.';
    const yes = confirm(text);
    if (yes) {
      setLoadingBtn('toggleVacation');
      setVacationMode(user.uid, !user.on_vacation)
        .then(() => router.refresh())
        .finally(() => setLoadingBtn(''));
    }
  };

  const changeUsername = async () => {
    const input = prompt('Enter new username');
    if (input) {
      setLoadingBtn('changeUsername');
      const usernameExists = await getUserByUsernameSlug(input);
      if (usernameExists) {
        alert('Username is already taken');
      } else {
        await updateUsername(user.uid, input);
        router.refresh();
      }
    }
  };

  const changePassword = async () => {
    const password = prompt('Enter new password');
    if (password) {
      setLoadingBtn('changePassword');
      await updateAuthUser({ uid: user.uid, email: user.email, password });
    }
    router.refresh();
  };

  const changeEmail = async () => {
    const email = prompt('Enter new email');
    if (email) {
      try {
        setLoadingBtn('changeEmail');
        await updateAuthUser({ uid: user.uid, email });
        await updateUserByKey(user.uid, 'email', email);
      } catch (e) {
        alert((e as Error)?.message || e);
      } finally {
        setLoadingBtn('');
      }
    }
  };

  const verifyEmail = async () => {
    try {
      setLoadingBtn('verifyEmail');
      await updateAuthUser({ uid: user.uid, emailVerified: true });
      router.refresh();
    } catch (e) {
      alert((e as Error)?.message || e);
      setLoadingBtn('');
    } finally {
      setLoadingBtn('');
    }
  };

  const verifyID = async () => {
    const yes = confirm(
      "Only click 'OK' if the Stripe Verification Session says 'Verified' when you search their full name."
    );
    if (!yes) return;
    try {
      setLoadingBtn('verifyID');
      await updateUserByKey(user.uid, 'id_status', 2);
      const is_verified = !!user.phone && !!authUser?.emailVerified;
      if (is_verified) await updateUserByKey(user.uid, 'is_verified', true);
      router.refresh();
    } catch (e) {
      alert((e as Error)?.message || e);
      setLoadingBtn('');
    }
  };

  const verifyPhone = async (phoneNumber: string) => {
    try {
      if (phoneNumber) {
        const data = await checkPhoneNumber({ phoneNumber });
        const { is_valid } = data.data;
        if (!is_valid) return alert('Invalid phone number');
        await updateAuthUser({ uid: user.uid, phoneNumber });
        await updateUserByKey(user.uid, 'phone', phoneNumber);
      } else {
        if (
          !window.confirm(
            'Verify without phone number? Only do this if you know the user has a phone number on the account already.'
          )
        ) {
          return;
        }
        // await updateUserByKey(user.uid, 'phone_verified', true);
      }
    } catch (e) {
      alert((e as Error)?.message || e);
    } finally {
      setLoadingBtn('');
    }
  };

  const chargeSeller = async () => {
    const input = prompt('Enter amount to charge');
    if (input) {
      if (isNaN(input as any)) return alert('Invalid amount');
      const description = prompt('Enter charge description (optional)');
      try {
        setLoadingBtn('chargeSeller');
        await chargeAccount({
          account_id: user.account_id!,
          amount: +input,
          description: description || '',
        });
      } catch (e) {
        alert((e as Error)?.message || e);
      }
      setLoadingBtn('');
    }
  };

  const addFunds = async () => {
    const input = prompt('Enter amount to add funds');
    if (input) {
      if (isNaN(input as any)) return alert('Invalid amount');
      try {
        setLoadingBtn('addFunds');
        await createTransfer(user.account_id!, +input);
      } catch (e) {
        alert((e as Error)?.message || e);
      }
      setLoadingBtn('');
    }
  };

  const setBan = async (banned: boolean) => {
    try {
      setLoadingBtn('setBan');
      await updateAuthUser({ uid: user.uid, disabled: banned });
      await setVacationMode(user.uid, banned);
      await updateUserByKey(user.uid, 'banned', banned);
      router.refresh();
    } catch (e) {
      alert((e as Error)?.message || e);
    } finally {
      setLoadingBtn('');
    }
  };

  const fetchProviders = async () => {
    const methods = await fetchSignInMethodsForEmail(AUTH, user.email);
    alert(methods.join(', '));
  };

  async function signOutUser() {
    if (!user) return;
    try {
      setLoadingBtn('signout');
      const success = await signOutUserCallable(user.uid);
      if (success) {
        showSuccessToast('User signed out');
      } else {
        showErrorToast('Error signing out user');
      }
    } catch (e) {
      showErrorToast('Error signing out user');
      logError(e);
    } finally {
      setLoadingBtn('');
    }
  }

  async function clickRating() {
    try {
      const input = prompt(
        "Enter a number 0-5.00 to add to this seller's rating"
      );
      if (input && user) {
        await updateRating(+input, user);
        showSuccessToast('Rating Updated.');
        router.refresh();
      }
    } catch (e) {
      showErrorToast('Error updating rating');
      console.error(e);
    }
  }

  async function changeRefundRate() {
    try {
      setLoadingBtn('changeRefundRate');
      const input = prompt(
        "Adjust the seller's refunds by how many? Enter a negative number to decrease the rate."
      );
      if (input && user) {
        await updateUserMetricsByKey(
          user.uid,
          'num_refunded',
          increment(+input)
        );
        showSuccessToast(
          'Refund Rate Updated. Please wait a couple of seconds then refresh.'
        );
      }
    } catch (e) {
      showErrorToast('Error updating refund rate');
      console.error(e);
    } finally {
      setLoadingBtn('');
    }
  }

  async function handleDeleteAllListings() {
    if (!userDoc) return;
    if (
      window.confirm(
        'Are you sure you want to delete all of the users listings? This is irreversible. If you want to temporarily remove listings, use vacation mode instead.'
      )
    ) {
      setLoadingBtn('deleteAllListings');
      deleteAllListingsMutation.mutate();
    }
  }

  return (
    <div
      className="flex flex-wrap items-center justify-center gap-5"
      style={{ zoom: 0.8 }}
    >
      {!user.banned && (
        <div className="flex items-center gap-4">
          <Toggle enabled={user.on_vacation} onChange={toggleVacation} />
          <h4>Vacation Mode</h4>
        </div>
      )}
      {!!user.customer_id && (
        <Button
          height="small"
          type="blue"
          width="small"
          text="Stripe Customer"
          href={`https://dashboard.stripe.com/customers/${user.customer_id}`}
          target="_blank"
        />
      )}
      {/* TODO Fetch From Client */}
      {!!user.account_activated && (
        <Button
          height="small"
          type="blue"
          width="small"
          text="Stripe Seller"
          href={`https://dashboard.stripe.com/connect/accounts/${user.account_id}`}
          target="_blank"
        />
      )}
      {!!user.verification_id && (
        <Button
          height="small"
          type="blue"
          width="small"
          text="Stripe Identity"
          href={`https://dashboard.stripe.com/identity/verification-sessions/${user.verification_id}`}
          target="_blank"
        />
      )}
      <Button
        height="small"
        type="gray"
        width="small"
        text="Backend"
        href={`https://console.firebase.google.com/project/${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}/firestore/data/~2Fusers~2F${user.uid}`}
        target="_blank"
      />
      <Button
        height="small"
        type="gray"
        width="small"
        text="View Purchases"
        href={`/dashboard/purchases?uid=${user.uid}`}
      />
      <Button
        height="small"
        type="gray"
        width="small"
        text="View Orders"
        href={`/dashboard/orders?uid=${user.uid}`}
      />
      <Button
        height="small"
        type="gray"
        width="small"
        text="View Messages"
        onClick={() => {
          setOpenModal('chat-history');
        }}
      />
      <Button
        loading={loadingBtn === 'impersonate'}
        height="small"
        type="gray"
        width="small"
        text="Impersonate"
        onClick={impersonate}
      />
      <Button
        loading={loadingBtn === 'signout'}
        height="small"
        type="gray"
        width="small"
        text="Sign Out"
        onClick={signOutUser}
      />
      <Button
        loading={loadingBtn === 'changeUsername'}
        height="small"
        type="gray"
        width="small"
        text="Change Username"
        onClick={changeUsername}
      />
      <Button
        loading={loadingBtn === 'changePassword'}
        height="small"
        type="gray"
        width="small"
        text="Change Password"
        onClick={changePassword}
      />
      <Button
        loading={loadingBtn === 'changeEmail'}
        height="small"
        type="gray"
        width="small"
        text="Change Email"
        onClick={changeEmail}
      />
      {!user?.phone ? (
        <>
          <Button
            loading={loadingBtn === 'verifyPhone'}
            height="small"
            type="gray"
            width="small"
            text="Verify Phone"
            onClick={() => {
              setShowPhoneInput(true);
            }}
          />
          {showPhoneInput && (
            <BaseModal
              isOpen={showPhoneInput}
              title="Enter Phone Number"
              dismiss={() => setShowPhoneInput(false)}
            >
              <div className="flex flex-col gap-[1.6rem] p-8">
                <Input
                  type="text"
                  placeholder="+15550001234"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
                <Button
                  text="Verify"
                  loading={loadingBtn === 'verifyPhone'}
                  onClick={async () => {
                    try {
                      if (phoneNumber) {
                        setLoadingBtn('verifyPhone');
                        const code = phoneNumber.startsWith('+')
                          ? undefined
                          : user?.country.code;
                        const parsed = parsePhoneNumber(phoneNumber, code);
                        await verifyPhone(parsed.number);
                      } else {
                        await verifyPhone('');
                      }
                      setShowPhoneInput(false);
                    } catch (e) {
                      alert(
                        'Invalid phone number. Please format it like this: +15550001234'
                      );
                    } finally {
                      setLoadingBtn('');
                    }
                  }}
                />
                <Button
                  type="text"
                  text={`Look up phone number ${phoneNumber}`}
                  onClick={() => {
                    try {
                      const code = phoneNumber.startsWith('+')
                        ? undefined
                        : user?.country.code;
                      const parsed = parsePhoneNumber(phoneNumber, code);
                      window.open(
                        `https://www.ipqualityscore.com/free-phone-number-lookup/lookup/US/${parsed.number}`
                      );
                    } catch (e) {
                      alert(e);
                    }
                  }}
                ></Button>
              </div>
            </BaseModal>
          )}
        </>
      ) : (
        <Button
          loading={loadingBtn === 'verifyPhone'}
          height="small"
          type="secondary"
          width="small"
          text="Unlink Phone"
          onClick={async () => {
            setLoadingBtn('verifyPhone');
            await updateAuthUser({ uid: user.uid, phoneNumber: null });
            // await updateUserByKey(user.uid, 'phone_verified', false);
            await updateUserByKey(user.uid, 'phone', '');
            await updateUserByKey(user.uid, 'is_verified', false);
            router.refresh();
            setLoadingBtn('');
          }}
        />
      )}
      {!authUser?.emailVerified && (
        <Button
          loading={
            loadingBtn === 'verifyEmail' || typeof authUser === 'undefined'
          }
          height="small"
          type="gray"
          width="small"
          text="Verify Email"
          onClick={verifyEmail}
        />
      )}
      {user.id_status !== 2 && (
        <Button
          loading={loadingBtn === 'verifyID'}
          height="small"
          type="gray"
          width="small"
          text="Verify ID"
          onClick={verifyID}
        />
      )}
      {!!user.account_id && (
        <>
          <Button
            loading={loadingBtn === 'chargeSeller'}
            height="small"
            type="secondary"
            width="small"
            text="Charge Seller"
            onClick={chargeSeller}
          />
          <Button
            loading={loadingBtn === 'addFunds'}
            height="small"
            type="gray"
            width="small"
            text="Add Funds"
            onClick={addFunds}
          />
        </>
      )}
      {user.banned ? (
        <Button
          loading={loadingBtn === 'setBan'}
          height="small"
          type="error"
          width="small"
          text="Unban"
          onClick={() => setBan(false)}
        />
      ) : (
        <Button
          loading={loadingBtn === 'setBan'}
          height="small"
          type="error"
          width="small"
          text="Ban"
          onClick={() => setBan(true)}
        />
      )}

      <Button
        text={'Update Seller Type'}
        height="small"
        type="tertiary"
        width="small"
        onClick={() => {
          setSellerType(user.seller_type ?? 'individual');
          setOpenModal('updateSellerType');
        }}
      />
      <Button
        text={'Fetch Providers For Email'}
        height="small"
        type="tertiary"
        width="small"
        onClick={fetchProviders}
        loading={loadingBtn === 'fetchProviders'}
      />
      <Button
        text="Unlink Provider"
        height="small"
        type="secondary"
        width="small"
        onClick={unlinkProvider}
        loading={loadingBtn === 'unlinkProvider'}
      />
      <Button
        text="View / Add Notes"
        height="small"
        type="tertiary"
        width="small"
        onClick={() => setOpenModal('notes')}
        loading={loadingBtn === 'viewNotes'}
      />
      <Button
        text="Change Rating"
        height="small"
        type="secondary"
        width="small"
        onClick={clickRating}
        loading={loadingBtn === 'changeRating'}
      />
      <Button
        text="Change Refund Rate"
        height="small"
        type="secondary"
        width="small"
        onClick={changeRefundRate}
        loading={loadingBtn === 'changeRefundRate'}
      />
      <Button
        text="Message User"
        height="small"
        type="tertiary"
        width="small"
        onClick={() => {
          openChatModalWithRecipientAndProductId({
            recipientUid: user.uid,
          });
        }}
        loading={loadingBtn === 'changeRating'}
      />
      <Button
        text="Delete All Listings"
        height="small"
        type="error"
        width="small"
        onClick={() => {
          handleDeleteAllListings();
        }}
        loading={loadingBtn === 'deleteAllListings'}
      />

      {openModal === 'notes' && (
        <BaseModal
          title="User Notes"
          isOpen={openModal === 'notes'}
          dismiss={() => setOpenModal('')}
        >
          <div className="flex max-h-[80vh] gap-[1.6rem] p-8">
            <div className="flex w-full flex-col gap-[2.4rem] rounded-2xl p-[2.4rem] lg:border-[1px]">
              <div className="text-[1.8rem flex w-full justify-between gap-[0.8rem] font-semibold">
                <span>Add Note</span>
                <Button
                  text="Save"
                  height="extraSmall"
                  width="extraSmall"
                  type="text"
                  onClick={() => {
                    addNewSellerNote(user.uid, newNote);
                    queryClient.invalidateQueries({
                      queryKey: ['user', user.uid],
                    });
                    setNewNote('');
                  }}
                  trailingIcon={<PencilSimpleIcon />}
                />
              </div>
              <div className="flex flex-col gap-[1.2rem]">
                <div className="flex w-full">
                  <span className="w-[10rem] text-brand-gray">Notes</span>
                  <span className="grow">
                    <textarea
                      className="h-[10rem] w-full rounded-lg border-[1px] border-gray-300 p-[1.2rem]"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add notes here..."
                    ></textarea>
                  </span>
                </div>
              </div>
            </div>
            {/* display all notes here */}

            {user?.notes && user.notes.length > 0 && (
              <div className="flex max-h-[80rem] w-full flex-col gap-[2.4rem] overflow-y-scroll rounded-2xl p-[2.4rem] lg:border-[1px]">
                <div className="text-[1.8rem flex w-full justify-between gap-[0.8rem] font-semibold">
                  <span>Previous Notes</span>
                </div>
                {user.notes
                  ?.sort((a, b) => b.created - a.created)
                  .map((note) => (
                    <div
                      className="flex flex-col gap-[1.2rem]"
                      key={note?.created}
                    >
                      <div className="flex w-full justify-between">
                        <div className="flex gap-[0.8rem]">
                          <span className="w-[10rem] text-brand-gray">
                            {new Date(note?.created!).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: 'numeric',
                              hour12: true,
                            })}
                          </span>
                          <span className="grow">{note?.text}</span>
                        </div>
                        <div className=" font-semibold text-brand-red">
                          <Button
                            text=""
                            type="text"
                            trailingIcon={<TrashIcon width={24} height={24} />}
                            onClick={() => {
                              deleteSellerNote(user.uid, note);
                              queryClient.invalidateQueries({
                                queryKey: ['user', user.uid],
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </BaseModal>
      )}
      {openModal === 'updateSellerType' && (
        <BaseModal
          isOpen={openModal === 'updateSellerType'}
          title={`Update Seller Type`}
          dismiss={() => setOpenModal('')}
        >
          <div className="flex w-full flex-col gap-[1.6rem] p-8">
            <span>
              Current Seller Type:
              <strong>{user.seller_type ?? 'none'}</strong>
            </span>
            <FormLabel value="Seller Type">
              <Select
                value={sellerType}
                options={[
                  {
                    label: 'Individual',
                    value: 'individual',
                    id: 'individual',
                  },
                  {
                    label: 'Dealer',
                    value: 'dealer',
                    id: 'dealer',
                  },
                  {
                    label: 'Privateer',
                    value: 'privateer',
                    id: 'privateer',
                  },
                  {
                    label: 'Small Business',
                    value: 'small_business',
                    id: 'small_business',
                  },
                ]}
                onChange={(e) => setSellerType(e.value)}
                allowEmpty={false}
              ></Select>
            </FormLabel>
            <Button
              text="Update"
              onClick={async () => {
                setLoadingBtn('updateSellerType');
                await updateSellerType(user.uid, sellerType);
                setOpenModal('');
                router.refresh();
                setLoadingBtn('');
              }}
              loading={loadingBtn === 'updateSellerType'}
            />
          </div>
        </BaseModal>
      )}
      {openModal === 'chat-history' && (
        <BaseModal
          isFullScreen
          isOpen={openModal === 'chat-history'}
          dismiss={() => setOpenModal('')}
        >
          <MessageCenter chats={allChats ?? []} variant={'orderDetail'} />
        </BaseModal>
      )}
    </div>
  );
};

export default SellerAdminControls;
